import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

// URL -> sayfa adı haritası
function getPageName(pathname) {
  if (pathname === '/dash' || pathname === '/dash/') return 'dashboard';
  if (pathname === '/login') return 'login';
  if (pathname === '/teklif-olustur' || pathname === '/dash/sepetim') return 'teklif_olustur';
  if (pathname.startsWith('/dash/')) {
    const view = pathname.replace('/dash/', '').split('/')[0];
    const map = {
      cariler: 'cariler', siparislerim: 'siparislerim', odeme: 'odeme',
      hesabim: 'hesabim', 'siparis-yonetimi': 'siparis_yonetimi',
      'fiyat-ayarlari': 'fiyat_ayarlari', 'stok-yonetimi': 'stok_yonetimi',
      'aktivite-loglar': 'aktivite_loglar', 'tur-yonetimi': 'tur_yonetimi'
    };
    return map[view] || view;
  }
  return pathname.replace(/^\//, '').replace(/\//g, '_') || 'home';
}

// Eleman için CSS selector hesapla - TIK ATILAN elemanı hedefler, parent'a çıkmaz
function computeSelector(el) {
  if (!el || el === document.body || el === document.documentElement) return null;

  // 1. Elemanın KENDİSİNDE data-tour varsa kullan
  if (el.getAttribute('data-tour')) return `[data-tour="${el.getAttribute('data-tour')}"]`;

  // 2. Elemanın KENDİSİNDE id varsa kullan
  if (el.id) return `#${el.id}`;

  // 3. Elemanın kendisi için benzersiz class-based selector dene
  if (el.className && typeof el.className === 'string') {
    const classes = el.className.split(' ').filter(c =>
      c && c.length < 50 &&
      !c.startsWith('hover:') && !c.startsWith('focus:') &&
      !c.startsWith('active:') && !c.startsWith('group-hover:') &&
      !c.startsWith('transition') && !c.startsWith('animate-') &&
      !c.startsWith('sm:') && !c.startsWith('md:') && !c.startsWith('lg:') && !c.startsWith('xl:')
    );
    // Önce tüm filtrelenmiş class'larla dene
    if (classes.length > 0) {
      const sel = el.tagName.toLowerCase() + '.' + classes.join('.');
      try { if (document.querySelectorAll(sel).length === 1) return sel; } catch(e) {}
    }
    // İlk 3 class ile dene
    if (classes.length > 3) {
      const sel = el.tagName.toLowerCase() + '.' + classes.slice(0, 3).join('.');
      try { if (document.querySelectorAll(sel).length === 1) return sel; } catch(e) {}
    }
  }

  // 4. Parent context ile birlikte selector oluştur (en yakın tanımlı parent + child path)
  const path = [];
  let current = el;
  for (let i = 0; i < 4 && current && current !== document.body; i++) {
    let seg = current.tagName.toLowerCase();

    // Bu seviyede benzersiz anchor var mı?
    if (current.getAttribute('data-tour')) {
      path.unshift(`[data-tour="${current.getAttribute('data-tour')}"]`);
      break;
    }
    if (current.id) {
      path.unshift(`#${current.id}`);
      break;
    }

    // Kardeşler arasında sıra ile belirle
    if (current.parentElement) {
      const siblings = Array.from(current.parentElement.children);
      const sameTag = siblings.filter(c => c.tagName === current.tagName);
      if (sameTag.length > 1) {
        const idx = siblings.indexOf(current) + 1;
        seg += `:nth-child(${idx})`;
      }
    }
    path.unshift(seg);
    current = current.parentElement;
  }
  return path.join(' > ');
}

// Eleman bilgi etiketi (hover sırasında gösterilir)
function getElementLabel(el) {
  if (!el) return '';
  let label = el.tagName.toLowerCase();
  if (el.getAttribute('data-tour')) label += `[data-tour="${el.getAttribute('data-tour')}"]`;
  else if (el.id) label += `#${el.id}`;
  else if (el.className && typeof el.className === 'string') {
    const cls = el.className.split(' ').filter(c => c && c.length < 30).slice(0, 2).join('.');
    if (cls) label += '.' + cls;
  }
  // İçerik text varsa göster
  const text = el.textContent?.trim();
  if (text && text.length < 40) label += ` "${text}"`;
  else if (text && text.length >= 40) label += ` "${text.substring(0, 35)}..."`;
  return label;
}

const POSITION_OPTIONS = [
  { value: 'top', label: 'Üst', icon: '↑' },
  { value: 'bottom', label: 'Alt', icon: '↓' },
  { value: 'left', label: 'Sol', icon: '←' },
  { value: 'right', label: 'Sağ', icon: '→' },
];

export default function TourEditorOverlay() {
  const { user, isRealAdmin, viewAsRole, setViewAsRole } = useUser();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [picking, setPicking] = useState(false);
  const [hoveredEl, setHoveredEl] = useState(null);
  const [selectedEl, setSelectedEl] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ target_selector: '', title: '', description: '', position: 'bottom', show_for_role: '' });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const highlightRef = useRef(null);
  const selectedHighlightRef = useRef(null);
  const driverRef = useRef(null);

  // Gerçek admin kontrolü (viewAsRole aktif olsa bile overlay görünür)
  const isAdmin = isRealAdmin;
  const pageName = getPageName(location.pathname);

  // Overlay kapanınca viewAsRole sıfırla
  useEffect(() => {
    if (!active && viewAsRole) {
      setViewAsRole(null);
    }
  }, [active, viewAsRole, setViewAsRole]);

  // Token her zaman gerçek kullanıcıdan (localStorage'dan)
  const getToken = useCallback(() => {
    return user?.token || JSON.parse(localStorage.getItem('user') || '{}').token;
  }, [user?.token]);

  // Adımları yükle
  const fetchSteps = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const token = getToken();
      const resp = await fetch(`/api/admin/tour-steps?page=${pageName}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSteps((data.steps || []).filter(s => s.page === pageName));
      }
    } catch (e) {}
    setLoading(false);
  }, [isAdmin, pageName, getToken]);

  useEffect(() => {
    if (active) fetchSteps();
  }, [active, fetchSteps]);

  // Hover highlight & element picking
  // depthOffset: scroll ile parent/child geçişi (0 = en küçük eleman, +1 = parent, +2 = grandparent...)
  const depthOffsetRef = useRef(0);

  useEffect(() => {
    if (!active || !picking) return;
    document.body.style.cursor = 'crosshair';
    depthOffsetRef.current = 0;
    let lastBaseEl = null;

    const allOverlayEls = () => document.querySelectorAll('.tour-editor-overlay-ui');

    const getElementUnder = (x, y) => {
      const overlays = allOverlayEls();
      overlays.forEach(el => { el.style.pointerEvents = 'none'; });
      if (highlightRef.current) highlightRef.current.style.pointerEvents = 'none';
      if (selectedHighlightRef.current) selectedHighlightRef.current.style.pointerEvents = 'none';
      const el = document.elementFromPoint(x, y);
      overlays.forEach(el => { el.style.pointerEvents = ''; });
      if (highlightRef.current) highlightRef.current.style.pointerEvents = '';
      if (selectedHighlightRef.current) selectedHighlightRef.current.style.pointerEvents = '';
      return el;
    };

    // Depth offset uygulayarak parent'a çık
    const getElAtDepth = (baseEl, depth) => {
      let el = baseEl;
      for (let i = 0; i < depth && el && el.parentElement && el.parentElement !== document.body; i++) {
        el = el.parentElement;
      }
      return el;
    };

    const onMove = (e) => {
      const baseEl = getElementUnder(e.clientX, e.clientY);
      if (!baseEl || baseEl.closest('.tour-editor-overlay-ui')) { setHoveredEl(null); return; }
      // Yeni bir eleman üzerine gelindiğinde depth sıfırla
      if (baseEl !== lastBaseEl) {
        depthOffsetRef.current = 0;
        lastBaseEl = baseEl;
      }
      const el = getElAtDepth(baseEl, depthOffsetRef.current);
      setHoveredEl(el);
    };

    const onScroll = (e) => {
      // Scroll ile parent/child geçişi
      if (!hoveredEl) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.deltaY > 0) {
        // Scroll down = parent'a çık (daha büyük eleman)
        depthOffsetRef.current = Math.min(depthOffsetRef.current + 1, 8);
      } else {
        // Scroll up = child'a in (daha küçük eleman)
        depthOffsetRef.current = Math.max(depthOffsetRef.current - 1, 0);
      }
      if (lastBaseEl) {
        const el = getElAtDepth(lastBaseEl, depthOffsetRef.current);
        setHoveredEl(el);
      }
    };

    const onClick = (e) => {
      if (e.target.closest('.tour-editor-overlay-ui')) return;
      e.preventDefault();
      e.stopPropagation();
      // Hover edilen (depth-adjusted) elemanı kullan
      const el = hoveredEl || getElementUnder(e.clientX, e.clientY);
      if (!el || el.closest('.tour-editor-overlay-ui')) return;
      const selector = computeSelector(el);
      setSelectedEl(el);
      setPicking(false);
      setFormOpen(true);
      setEditingId(null);
      setForm({ target_selector: selector || '', title: '', description: '', position: 'bottom', show_for_role: '' });
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('wheel', onScroll, { capture: true, passive: false });
    return () => {
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('wheel', onScroll, { capture: true });
    };
  }, [active, picking, hoveredEl]);

  // Hover highlight box + tooltip
  const tooltipRef = useRef(null);
  useEffect(() => {
    if (!highlightRef.current) return;
    if (hoveredEl && picking) {
      const rect = hoveredEl.getBoundingClientRect();
      highlightRef.current.style.display = 'block';
      highlightRef.current.style.top = rect.top + window.scrollY + 'px';
      highlightRef.current.style.left = rect.left + window.scrollX + 'px';
      highlightRef.current.style.width = rect.width + 'px';
      highlightRef.current.style.height = rect.height + 'px';
      // Tooltip
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'block';
        tooltipRef.current.style.top = (rect.top + window.scrollY - 32) + 'px';
        tooltipRef.current.style.left = (rect.left + window.scrollX) + 'px';
        tooltipRef.current.textContent = getElementLabel(hoveredEl);
      }
    } else {
      highlightRef.current.style.display = 'none';
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    }
  }, [hoveredEl, picking]);

  // Selected highlight box
  useEffect(() => {
    if (!selectedHighlightRef.current) return;
    if (selectedEl && !picking) {
      const rect = selectedEl.getBoundingClientRect();
      selectedHighlightRef.current.style.display = 'block';
      selectedHighlightRef.current.style.top = rect.top + window.scrollY + 'px';
      selectedHighlightRef.current.style.left = rect.left + window.scrollX + 'px';
      selectedHighlightRef.current.style.width = rect.width + 'px';
      selectedHighlightRef.current.style.height = rect.height + 'px';
    } else {
      selectedHighlightRef.current.style.display = 'none';
    }
  }, [selectedEl, picking]);

  // Kaydet
  const handleSave = async () => {
    if (!form.title || !form.description) { alert('Başlık ve açıklama zorunlu'); return; }
    setSaving(true);
    try {
      const token = getToken();
      const maxOrder = steps.reduce((mx, s) => Math.max(mx, s.step_order), 0);
      const body = { ...form, page: pageName, step_order: editingId ? form.step_order : maxOrder + 1, is_active: 1 };
      const url = editingId ? `/api/admin/tour-steps/${editingId}` : '/api/admin/tour-steps';
      const method = editingId ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (resp.ok) {
        await fetchSteps();
        setFormOpen(false);
        setSelectedEl(null);
        setEditingId(null);
        setForm({ target_selector: '', title: '', description: '', position: 'bottom', show_for_role: '' });
      }
    } catch (e) { alert('Kayıt hatası'); }
    setSaving(false);
  };

  // Sil
  const handleDelete = async (id) => {
    if (!window.confirm('Bu adımı silmek istediğinize emin misiniz?')) return;
    try {
      const token = getToken();
      await fetch(`/api/admin/tour-steps/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      await fetchSteps();
    } catch (e) {}
  };

  // Adıma tıkla - highlight
  const highlightStep = (step) => {
    if (!step.target_selector) return;
    const el = document.querySelector(step.target_selector);
    if (el) {
      setSelectedEl(el);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Düzenle
  const editStep = (step) => {
    setEditingId(step.id);
    setForm({
      target_selector: step.target_selector || '', title: step.title, description: step.description,
      position: step.position || 'bottom', show_for_role: step.show_for_role || '', step_order: step.step_order
    });
    setFormOpen(true);
    if (step.target_selector) {
      const el = document.querySelector(step.target_selector);
      if (el) setSelectedEl(el);
    }
  };

  // Önizle
  const previewTour = () => {
    if (steps.length === 0) { alert('Bu sayfa için tur adımı yok'); return; }
    // Overlay'ı geçici kapat
    setActive(false);
    setTimeout(() => {
      const driverSteps = steps
        .filter(s => s.is_active && (!s.target_selector || document.querySelector(s.target_selector)))
        .map(s => ({
          element: s.target_selector || undefined,
          popover: { title: s.title, description: s.description, side: s.position || 'bottom', align: 'start' }
        }));
      if (driverSteps.length === 0) { alert('Gösterilecek aktif adım yok'); setActive(true); return; }
      if (driverRef.current) try { driverRef.current.destroy(); } catch (e) {}
      const d = driver({
        showProgress: true, showButtons: ['next', 'previous', 'close'],
        nextBtnText: 'Sonraki', prevBtnText: 'Önceki', doneBtnText: 'Tamamla',
        progressText: '{{current}} / {{total}}',
        overlayColor: 'rgba(0, 0, 0, 0.6)', stagePadding: 8, stageRadius: 8,
        onDestroyStarted: () => { d.destroy(); setActive(true); },
        steps: driverSteps
      });
      driverRef.current = d;
      d.drive();
    }, 300);
  };

  // Sıralama değiştir
  const moveStep = async (idx, dir) => {
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= steps.length) return;
    try {
      const token = getToken();
      await fetch('/api/admin/tour-steps-reorder', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ items: [
          { id: steps[idx].id, step_order: steps[swapIdx].step_order },
          { id: steps[swapIdx].id, step_order: steps[idx].step_order }
        ]})
      });
      await fetchSteps();
    } catch (e) {}
  };

  if (!isAdmin) return null;

  // Floating button (inactive state)
  if (!active) {
    return (
      <button
        onClick={() => setActive(true)}
        className="tour-editor-overlay-ui fixed bottom-6 left-6 z-[9990] bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all px-4 py-2.5 flex items-center gap-2 text-sm font-semibold hover:scale-105 border border-violet-400/30"
        title="Tur düzenleyiciyi aç"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" /></svg>
        Tur Düzenle
      </button>
    );
  }

  return (
    <>
      {/* Hover highlight box */}
      <div ref={highlightRef} style={{ display: 'none', position: 'absolute', border: '3px solid #7c3aed', borderRadius: 8, pointerEvents: 'none', zIndex: 99990, background: 'rgba(124,58,237,0.08)', transition: 'all 0.1s ease' }} />
      {/* Hover tooltip - eleman bilgisi */}
      <div ref={tooltipRef} style={{ display: 'none', position: 'absolute', zIndex: 99991, background: '#1e1b4b', color: '#e9d5ff', fontSize: 11, fontFamily: 'monospace', padding: '3px 8px', borderRadius: 6, pointerEvents: 'none', whiteSpace: 'nowrap', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }} />
      {/* Selected highlight box */}
      <div ref={selectedHighlightRef} style={{ display: 'none', position: 'absolute', border: '3px solid #2563eb', borderRadius: 8, pointerEvents: 'none', zIndex: 99990, background: 'rgba(37,99,235,0.1)', boxShadow: '0 0 0 4px rgba(37,99,235,0.15)', transition: 'all 0.1s ease' }} />

      {/* Picking mode indicator */}
      {picking && (
        <div className="tour-editor-overlay-ui" style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 99999, background: 'rgba(124,58,237,0.95)', color: '#fff', padding: '12px 24px', borderRadius: 12, fontSize: 12, fontWeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.3)', pointerEvents: 'none', textAlign: 'center', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>Eleman Seçim Modu</div>
          <div>Tıklayın: Seç &nbsp;|&nbsp; Scroll: Büyük/Küçük eleman seç</div>
        </div>
      )}

      {/* Top Bar */}
      <div className="tour-editor-overlay-ui fixed top-0 left-0 right-0 z-[99995] bg-gradient-to-r from-violet-700 to-purple-800 text-white shadow-lg" style={{ height: 48 }}>
        <div className="flex items-center justify-between h-full px-4 max-w-full">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-violet-200" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" /></svg>
            <span className="font-bold text-sm">Tur Düzenleyici</span>
            <span className="bg-white/20 rounded-full px-3 py-0.5 text-xs font-medium">{pageName}</span>
            <span className="text-violet-200 text-xs">{steps.length} adım</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Görünüm Değiştirme Toggle */}
            <div className="flex items-center bg-white/10 rounded-lg p-0.5 border border-white/20">
              <button
                onClick={() => { if (viewAsRole) setViewAsRole(null); }}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${!viewAsRole ? 'bg-white text-violet-800 shadow-sm' : 'text-white/70 hover:text-white'}`}
              >
                Admin
              </button>
              <button
                onClick={() => setViewAsRole('member')}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${viewAsRole === 'member' ? 'bg-amber-400 text-amber-900 shadow-sm' : 'text-white/70 hover:text-white'}`}
              >
                Bayi Görünümü
              </button>
            </div>
            {viewAsRole && (
              <span className="bg-amber-400/20 text-amber-200 text-[10px] px-2 py-0.5 rounded-full border border-amber-400/30 animate-pulse">
                Bayi olarak görüntüleniyor
              </span>
            )}
            <div className="w-px h-6 bg-white/20"></div>
            <button
              onClick={() => { setPicking(true); setFormOpen(false); setSelectedEl(null); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${picking ? 'bg-white text-violet-800' : 'bg-white/20 text-white hover:bg-white/30'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672Zm-7.518-.267A8.25 8.25 0 1 1 20.25 10.5M8.288 14.212A5.25 5.25 0 1 1 17.25 10.5" /></svg>
              {picking ? 'Eleman Seçiliyor...' : 'Eleman Seç'}
            </button>
            <button onClick={previewTour} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition flex items-center gap-1.5" disabled={steps.length === 0}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" /></svg>
              Önizle
            </button>
            <button onClick={() => { setActive(false); setPicking(false); setFormOpen(false); setSelectedEl(null); setHoveredEl(null); }} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/80 text-white hover:bg-red-600 transition flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              Çık
            </button>
          </div>
        </div>
      </div>

      {/* Left Sidebar - Steps List */}
      <div className="tour-editor-overlay-ui fixed left-0 top-12 bottom-0 z-[99995] bg-white border-r border-gray-200 shadow-xl overflow-y-auto" style={{ width: 280 }}>
        <div className="p-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Tur Adımları</span>
            <button
              onClick={() => { setPicking(true); setFormOpen(false); setSelectedEl(null); setEditingId(null); }}
              className="text-[10px] font-semibold px-2 py-1 rounded bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition"
            >
              + Ekle
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
          </div>
        ) : steps.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-xs">
            <p className="mb-2">Henüz adım yok.</p>
            <p>"Eleman Seç" ile başlayın.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className="p-3 hover:bg-violet-50/50 transition cursor-pointer group"
                onClick={() => highlightStep(step)}
              >
                <div className="flex items-start gap-2">
                  {/* Sıra + Hareket */}
                  <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-0.5">
                    <button onClick={(e) => { e.stopPropagation(); moveStep(idx, 'up'); }} disabled={idx === 0} className="text-gray-300 hover:text-violet-600 disabled:opacity-20 transition">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" /></svg>
                    </button>
                    <span className="text-[10px] font-bold text-violet-600 bg-violet-50 w-5 h-5 flex items-center justify-center rounded-full">{idx + 1}</span>
                    <button onClick={(e) => { e.stopPropagation(); moveStep(idx, 'down'); }} disabled={idx === steps.length - 1} className="text-gray-300 hover:text-violet-600 disabled:opacity-20 transition">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                    </button>
                  </div>
                  {/* İçerik */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[11px] text-gray-900 truncate">{step.title}</div>
                    <div className="text-[10px] text-gray-400 truncate mt-0.5">{step.description}</div>
                    {step.target_selector && (
                      <code className="text-[9px] text-violet-500 bg-violet-50 px-1 py-0.5 rounded mt-1 inline-block truncate max-w-full">{step.target_selector}</code>
                    )}
                  </div>
                  {/* Aksiyonlar */}
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                    <button onClick={(e) => { e.stopPropagation(); editStep(step); }} className="text-blue-500 hover:text-blue-700 transition" title="Düzenle">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" /></svg>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(step.id); }} className="text-red-400 hover:text-red-600 transition" title="Sil">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right Panel - Form */}
      {formOpen && (
        <div className="tour-editor-overlay-ui fixed right-0 top-12 z-[99995] bg-white border-l border-gray-200 shadow-xl overflow-y-auto" style={{ width: 340, bottom: 0 }}>
          <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-violet-50">
            <h3 className="font-bold text-sm text-gray-900">{editingId ? 'Adımı Düzenle' : 'Yeni Tur Adımı'}</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Seçilen elemana bir tur adımı ekleyin</p>
          </div>
          <div className="p-4 space-y-4">
            {/* Selector */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Hedef Selector</label>
              <div className="flex gap-1">
                <input
                  type="text" className="flex-1 px-2.5 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:ring-2 focus:ring-violet-400 focus:border-violet-400 bg-gray-50"
                  value={form.target_selector} onChange={e => setForm(f => ({ ...f, target_selector: e.target.value }))}
                  placeholder='[data-tour="..."]'
                />
                <button onClick={() => { setPicking(true); setFormOpen(false); }} className="px-2 py-2 bg-violet-50 text-violet-700 rounded-lg border border-violet-200 hover:bg-violet-100 transition" title="Tekrar seç">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672Z" /></svg>
                </button>
              </div>
            </div>
            {/* Başlık */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Başlık *</label>
              <input type="text" className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-400" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Hoşgeldiniz!" />
            </div>
            {/* Açıklama */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Açıklama *</label>
              <textarea className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-400" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Bu alanda siparişlerinizi takip edebilirsiniz..." />
            </div>
            {/* Pozisyon */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Pozisyon</label>
              <div className="grid grid-cols-4 gap-1.5">
                {POSITION_OPTIONS.map(p => (
                  <button key={p.value} onClick={() => setForm(f => ({ ...f, position: p.value }))}
                    className={`py-2 rounded-lg text-xs font-semibold transition border ${form.position === p.value ? 'bg-violet-600 text-white border-violet-600 shadow-sm' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                  >
                    <span className="text-base">{p.icon}</span>
                    <div className="text-[9px] mt-0.5">{p.label}</div>
                  </button>
                ))}
              </div>
            </div>
            {/* Rol */}
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Gösterilecek Rol</label>
              <select className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm" value={form.show_for_role} onChange={e => setForm(f => ({ ...f, show_for_role: e.target.value }))}>
                <option value="">Herkes</option>
                <option value="admin">Sadece Admin</option>
                <option value="member">Sadece Bayi</option>
              </select>
            </div>
            {/* Butonlar */}
            <div className="flex gap-2 pt-2">
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition disabled:opacity-50">
                {saving ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Kaydet')}
              </button>
              <button onClick={() => { setFormOpen(false); setSelectedEl(null); setEditingId(null); }} className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition">
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
